import { Select } from 'antd';
import { useNotify } from 'react-admin';
import { useListContext } from 'ra-core';
import { useModalContext } from '@providers';
import React, { useEffect, useState } from 'react';

import { useRequest } from '@hooks';
import { cropHttp } from '@network';
import cls from '@pages/loan-customer-data/CustomerData.module.css';
import { BlueButton, BlueOutlineButton } from '@components';
import { CropLevels } from '@types';

enum BulkProps {
  'Category', // select
  'Type', // select
  'Yiel (Ton)', // select
  'Average Cost Per Ha', // select
  'Average Price', // select
  'Varieties', // tags
}

type BulkProp = keyof typeof BulkProps;

const bulkPropsList = Object.keys(BulkProps).filter(k => isNaN(+k) || isNaN(parseFloat(k)));
type Option = {value: number | string; label: string};

export const CropListActions = () => {
  const notify = useNotify();
  const { hideModal } = useModalContext();
  const { selectedIds, refetch } = useListContext();

  const [prop, setProp] = useState<BulkProp>();
  const [value, setValue] = useState<string | number | string[] | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);

  const resetValues = () => {
    setValue(null);
  };

  const onCancel = () => {
    setProp(undefined);
    resetValues();
    hideModal();
  };

  const { loading, submit } = useRequest(async () => {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return notify('No records are selected');
    }
    if (!prop) {
      return notify('No property is selected');
    }

    const updatedTotal = await cropHttp.updateBulk({ selectedIds, prop, value });
    if (selectedIds.length !== updatedTotal) {
      notify(`Updated: ${updatedTotal} of ${selectedIds.length} records. Some records can't be changed because of the contract status of related credit.`);
    } else {
      notify(`Updated records: ${updatedTotal}`);
    }

    onCancel();
    refetch();
  });

  const bulkPropsSorted = bulkPropsList
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  useEffect(() => {
    resetValues();
  }, [prop]);

  useEffect(() => {
    (async () => {
      const result = await cropHttp.getAllCategories();
      const sorted = result.map(bank => ({
        value: bank.id,
        label: bank.nameMx as string,
      })).sort((a, b) => a.label > b.label ? 1 : -1);
      setCategoryOptions(sorted);
    })();
  }, []);

  return (
    <div className={cls.box}>
      <div className={cls.title}>Properties to update</div>
      <Select
        value={prop}
        onChange={v => setProp(v)}
        className={cls.select}
        placeholder="Select a property to edit">
        {bulkPropsSorted.map(propName => (
          <Select.Option key={propName} value={propName}>{propName}</Select.Option>
        ))}
      </Select>

      <div className={cls.content}>
        <div className={cls.title}>
          <div>{prop}</div>
        </div>
        {prop === 'Category' && (
          <Select
            value={value}
            onChange={v => setValue(v)}
            className={cls.select}
            placeholder="Category">
            {categoryOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
            ))}
          </Select>
        )}
        {prop === 'Type' && (
          <Select
            value={value}
            onChange={v => setValue(v)}
            className={cls.select}
            placeholder="Type">
            <Select.Option value="Intensivo">Intensivo</Select.Option>
            <Select.Option value="Extensivo">Extensivo</Select.Option>
          </Select>
        )}
        {prop === 'Varieties' && (
          <Select
            mode="tags"
            value={value as string[] || []}
            onChange={v => setValue(v)}
            className={cls.select}
            tokenSeparators={[',']}
          />
        )}
        {prop && prop !== 'Type' && prop !== 'Category' && prop !== 'Varieties' && (
          <Select
            value={value}
            onChange={v => setValue(v)}
            className={cls.select}
            placeholder={prop}>
            <Select.Option value={CropLevels.LOW}>{CropLevels.LOW}</Select.Option>
            <Select.Option value={CropLevels.MEDIUM_LOW}>{CropLevels.MEDIUM_LOW}</Select.Option>
            <Select.Option value={CropLevels.MEDIUM}>{CropLevels.MEDIUM}</Select.Option>
            <Select.Option value={CropLevels.MEDIUM_HIGH}>{CropLevels.MEDIUM_HIGH}</Select.Option>
            <Select.Option value={CropLevels.HIGH}>{CropLevels.HIGH}</Select.Option>
          </Select>
        )}
      </div>

      <div className={cls.controls}>
        <BlueButton className={cls.btn} onClick={submit} disabled={loading}>Update</BlueButton>
        <BlueOutlineButton className={cls.btn} onClick={onCancel}>Cancel</BlueOutlineButton>
      </div>
    </div>
  );
};
